import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { formatCompactNumber } from '../utils/formatters';
import { getLanguageColor } from '../utils/chartColors';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const NEUTRAL_COLOR = 'rgba(180, 180, 180, 0.45)';
const NEUTRAL_BORDER = 'rgba(180, 180, 180, 0.6)';
const HOVER_COLOR = 'rgba(175, 62, 255, 0.8)';
const HOVER_BORDER = '#af3eff';

const PublishedBooksChart = ({ data, viewType = 'daily', periodLabel = 'Last 7 Days', onViewChange, isDarkMode }) => {
  const chartRef = useRef(null);

  const totalBooks = data.reduce((sum, item) => sum + item.count, 0);

  // Collect all unique languages sorted by total count descending
  const languageTotals = {};
  data.forEach(item => {
    Object.entries(item.languageCounts || {}).forEach(([lang, cnt]) => {
      languageTotals[lang] = (languageTotals[lang] || 0) + cnt;
    });
  });
  const allLanguages = Object.entries(languageTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([lang]) => lang);

  // Build a color map using the centralized fixed language colors
  const languageColorMap = {};
  allLanguages.forEach((lang) => {
    languageColorMap[lang] = getLanguageColor(lang, isDarkMode);
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Single dataset — one bar per date showing total count
  const chartData = {
    labels: data.map(item => item.label || formatDate(item.date)),
    datasets: [
      {
        label: 'Published Books',
        data: data.map(item => item.count),
        backgroundColor: NEUTRAL_COLOR,
        borderColor: NEUTRAL_BORDER,
        borderWidth: 1,
        borderRadius: 8,
        hoverBackgroundColor: HOVER_COLOR,
        hoverBorderColor: HOVER_BORDER,
        hoverBorderWidth: 2,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(30, 30, 30, 0.95)',
        padding: 14,
        titleColor: '#fff',
        bodyColor: '#ccc',
        borderColor: '#af3eff',
        borderWidth: 1,
        displayColors: true,
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 4,
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const label = tooltipItems[0].label;
            const total = tooltipItems[0].parsed.y;
            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
              const dayName = new Date(label + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
              return [`${label}  (${dayName})`, `Total: ${total}`];
            }
            return [`${label}`, `Total: ${total}`];
          },
          // Replace default single label with per-language lines
          label: () => null,
          afterBody: (tooltipItems) => {
            if (!tooltipItems || tooltipItems.length === 0) return [];
            const idx = tooltipItems[0].dataIndex;
            const item = data[idx];
            if (!item || !item.languageCounts) return [];

            // Sort languages by count descending for this date
            const entries = Object.entries(item.languageCounts)
              .filter(([, cnt]) => cnt > 0)
              .sort((a, b) => b[1] - a[1]);

            return entries.map(([lang, cnt]) => `${lang.toUpperCase()}: ${cnt}`);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#818181',
          font: { size: 12 },
          stepSize: 5
        },
        grid: {
          color: 'rgba(129, 129, 129, 0.1)',
          drawBorder: false
        }
      },
      x: {
        ticks: {
          color: '#818181',
          font: { size: 12 }
        },
        grid: {
          display: false
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Custom plugin: draw colored segments inside the hovered bar
  // Uses afterDatasetsDraw so segments appear under the tooltip (which draws in afterDraw)
  const hoverSegmentPlugin = {
    id: 'hoverSegment',
    afterDatasetsDraw: (chart) => {
      const activeElements = chart.tooltip?.getActiveElements?.() || [];
      if (activeElements.length === 0) return;

      const hoveredIdx = activeElements[0].index;
      const item = data[hoveredIdx];
      if (!item || !item.languageCounts) return;

      const meta = chart.getDatasetMeta(0);
      const bar = meta.data[hoveredIdx];
      if (!bar) return;

      const { x, y, width, base } = bar.getProps(['x', 'y', 'width', 'base']);
      const barHeight = base - y;
      if (barHeight <= 0 || item.count === 0) return;

      const ctx = chart.ctx;
      const barWidth = width;
      const barLeft = x - barWidth / 2;
      const radius = 8;

      // Sort languages by count descending
      const entries = Object.entries(item.languageCounts)
        .filter(([, cnt]) => cnt > 0)
        .sort((a, b) => b[1] - a[1]);

      // Draw colored segments from bottom to top
      let currentY = base;
      ctx.save();

      // Clip to the rounded bar shape
      ctx.beginPath();
      const barTop = y;
      const barBottom = base;
      // Rounded rect path (top corners only)
      ctx.moveTo(barLeft, barBottom);
      ctx.lineTo(barLeft, barTop + radius);
      ctx.quadraticCurveTo(barLeft, barTop, barLeft + radius, barTop);
      ctx.lineTo(barLeft + barWidth - radius, barTop);
      ctx.quadraticCurveTo(barLeft + barWidth, barTop, barLeft + barWidth, barTop + radius);
      ctx.lineTo(barLeft + barWidth, barBottom);
      ctx.closePath();
      ctx.clip();

      entries.forEach(([lang, cnt]) => {
        const segmentHeight = (cnt / item.count) * barHeight;
        const color = languageColorMap[lang] || '#888';

        ctx.fillStyle = color;
        ctx.fillRect(barLeft, currentY - segmentHeight, barWidth, segmentHeight);

        currentY -= segmentHeight;
      });

      ctx.restore();
    }
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>Published Books</h3>
        <div className="chart-header-controls">
          {onViewChange && (
            <div className="chart-view-toggle">
              {[{ id: 'daily', label: 'D', title: 'Daily' }, { id: 'weekly', label: 'W', title: 'Weekly' }, { id: 'monthly', label: 'M', title: 'Monthly' }].map(view => (
                <label key={view.id} className={`chart-view-option ${viewType === view.id ? 'active' : ''}`} data-tooltip={view.title}>
                  <input
                    type="radio"
                    name="publishedViewHome"
                    value={view.id}
                    checked={viewType === view.id}
                    onChange={(e) => onViewChange(e.target.value)}
                  />
                  {view.label}
                </label>
              ))}
            </div>
          )}
          <span className="chart-badge">
            {formatCompactNumber(totalBooks)} books
          </span>
        </div>
      </div>
      <div className="chart-wrapper">
        <Bar data={chartData} options={options} plugins={[hoverSegmentPlugin]} ref={chartRef} />
      </div>
    </div>
  );
};

export default PublishedBooksChart;
